import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { fetchWithAuth } from '../../utils/api';
import { ShoppingCart, Plus, Search, Pen, Trash2, Eye, X as IconX, Package, Hash, Truck, Percent, IndianRupee, Check, ArrowDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  findWarehouse, findWhPurchase,
  catalogProductsOf, variantLabelOf,
  whPurchaseOrdersOf, addWhPurchaseOrder, updateWhPurchaseOrder, deleteWhPurchaseOrder,
  suppliersOf,
} from '../../data/store';
import { CustomSelect } from '../../components/CustomSelect';
import { CustomDatePickerFixed } from '../../components/CustomDatePickerFixed';
import { ConfirmModal } from '../../components/ConfirmModal';
import { FilterPopover } from '../../components/FilterPopover';
import { IconAction, RowActions } from '../../components/RowActions';



// A labelled field, and its error message underneath.
//
// `error` is here because of what its absence cost. Field passes its box styling
// - background, border, radius, padding - down to whatever child it is given, so
// a field that also had to show a validation message wrapped the control and the
// message together in a plain <div>, and that <div> is what received the box.
// The control inside then drew its own, and Supplier, Status and Product each
// showed two nested boxes; quantity and price showed a styled box around a
// completely unstyled <input>, which never received the class at all. Variant,
// which has no message to show and so no wrapper, was the only one of the seven
// that looked right.
//
// The message belongs to the field, not to the control, so the field prints it.
const Field = ({ label, icon: Icon, children, bg = 'bg-gray-50', error }) => (
  <label className="block space-y-1">
    <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">{label}</span>
    <span className="relative block">
      {Icon && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 z-30 pointer-events-none" aria-hidden="true">
          <Icon size={14} />
        </span>
      )}
      {React.cloneElement(children, {
        'aria-invalid': error ? true : undefined,
        className: `w-full rounded-lg ${bg} border ${error ? 'border-rose-400 focus:border-rose-500' : 'border-gray-100 focus:border-[var(--theme-primary)]'} focus:bg-white outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d] transition-colors ${Icon ? 'pl-9' : ''} ${children.props.className || ''}`,
      })}
    </span>
    {error && <p className="text-[11px] font-medium text-rose-500">{error}</p>}
  </label>
);

Field.propTypes = {
  label: PropTypes.string.isRequired,
  icon: PropTypes.elementType,
  children: PropTypes.element.isRequired,
  bg: PropTypes.string,
  error: PropTypes.string,
};

const emptyLine = () => ({
  productId: '', variantId: '', purchaseType: 'item',
  billedQty: 1,
  originalPrice: 0, discountAmount: 0, flatDiscountPerItem: 0, discountPercentage: 0, discountType: 'flat',
  freeQtyPerUnit: 0, totalFreeQty: 0,
  unitDetails: '[]'
});

const blankPO = (warehouseId, orgId) => ({
  id: null,
  warehouseId,
  orgId,
  supplier: '',
  date: new Date().toISOString().slice(0, 10),
  status: 'Pending',
  lines: [emptyLine()]
});

const formatDateTime = (dateString) => {
  if (!dateString) return '—';
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    return new Intl.DateTimeFormat('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true
    }).format(d);
  // eslint-disable-next-line no-unused-vars
  } catch (_e) {
    return dateString;
  }
};

const calculateCost = (originalPrice, discountAmount, discountPercentage, discountType, billedQty = 1) => {
  let cost = Number(originalPrice) || 0;
  if (discountType === 'flat') {
    let flatTotal = Number(discountAmount) || 0;
    let qty = Number(billedQty) || 1;
    cost = Math.max(0, cost - (flatTotal / qty));
  } else if (discountType === 'percentage') {
    let pct = Number(discountPercentage) || 0;
    if (pct > 0) {
      cost = Math.max(0, cost - (cost * pct / 100));
    }
  }
  return cost;
};

const lineAmt = (ln) => {
  const cost = calculateCost(ln.originalPrice, ln.discountAmount, ln.discountPercentage, ln.discountType, ln.billedQty);
  return +(Number(ln.billedQty || 0) * cost).toFixed(2);
};

const POForm = ({ draft, setDraft, onClose, onSubmit, mode, accent, products, suppliers, globalUnits = [], showNotification }) => {
  const [errors, setErrors] = useState({});
  useEffect(() => { setErrors({}); }, [draft.id, mode]);

  const getUnitName = (id) => globalUnits.find(u => u.id === id)?.name || id || 'Unit';

  const setLine = (index, updates) => {
    const newLines = [...draft.lines];
    newLines[index] = { ...newLines[index], ...updates };
    setDraft({ ...draft, lines: newLines });
  };

  const addLine = () => {
    setDraft({ ...draft, lines: [...draft.lines, emptyLine()] });
  };

  const removeLine = (index) => {
    if (draft.lines.length <= 1) return;
    const newLines = [...draft.lines];
    newLines.splice(index, 1);
    setDraft({ ...draft, lines: newLines });
  };

  const handleFormSubmit = () => {
    const errs = {};
    if (!draft.supplier) errs.supplier = 'Please select a supplier.';
    if (!draft.status) errs.status = 'Please select a status.';
    
    const lines = draft.lines || [draft];
    lines.forEach((ln, idx) => {
      if (!ln.productId) errs[`line_${idx}_productId`] = 'Product is required.';
      if (Number(ln.billedQty) <= 0) errs[`line_${idx}_billedQty`] = 'Billed quantity must be > 0.';
      if (ln.originalPrice === '' || ln.originalPrice === null) errs[`line_${idx}_originalPrice`] = 'Price is required.';
    });

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      if (showNotification) showNotification('Please fix the highlighted fields.', 'error');
      return;
    }
    setErrors({});
    const finalAmt = lines.reduce((sum, ln) => sum + lineAmt(ln), 0);
    onSubmit({ ...draft, amount: finalAmt });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      className="w-full bg-white rounded-3xl shadow-sm border border-[var(--theme-tertiary)] overflow-hidden flex flex-col mb-4"
    >
      <header className="px-6 py-4 border-b border-[var(--theme-tertiary)] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg text-white flex items-center justify-center" style={{ background: accent }}><ShoppingCart size={18} /></div>
          <div>
            <h3 className="text-lg font-extrabold text-[#0d0d0d]">{mode === 'edit' ? 'Edit purchase order' : mode === 'view' ? 'View purchase order' : 'New purchase order'}</h3>
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">Add multiple items to this purchase order</p>
          </div>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="p-2 rounded-full text-slate-400 hover:text-rose-500 hover:bg-rose-50"><IconX size={16} /></button>
      </header>
      
      <div className={`px-6 py-5 overflow-y-auto custom-scrollbar flex flex-col gap-6 ${mode === 'view' ? 'pointer-events-none' : ''}`}>
        
        {/* HEADER DETAILS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Supplier *" bg="bg-white" error={errors.supplier}>
            <CustomSelect
              value={draft.supplier}
              onChange={(v) => { setDraft({ ...draft, supplier: v }); setErrors({ ...errors, supplier: undefined }); }}
              placeholder="Select a supplier…"
              options={suppliers.map((s) => ({ value: s.supplierName, label: s.supplierName }))}
            />
          </Field>
          <div className="relative">
            <span className="block mb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Date</span>
            <CustomDatePickerFixed value={draft.date} onChange={(val) => setDraft({ ...draft, date: val })} />
          </div>
          <Field label="Status *" bg="bg-white" error={errors.status}>
            <CustomSelect
              value={draft.status}
              onChange={(v) => { setDraft({ ...draft, status: v }); setErrors({ ...errors, status: undefined }); }}
              placeholder="Select status…"
              options={[
                { value: 'Pending', label: 'Pending' },
                { value: 'Received', label: 'Received' }
              ]}
            />
          </Field>
        </div>

        <div className="h-px bg-slate-200 w-full my-2"></div>

        {/* LINE ITEMS */}
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <span className="text-[12px] font-extrabold text-[#0d0d0d] flex items-center gap-2"><Package size={16} style={{ color: accent }}/> Line Items ({(draft.lines || [draft]).length})</span>
            {mode !== 'view' && (
              <button type="button" onClick={addLine} className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.12em] hover:opacity-80 transition-opacity" style={{ color: accent }}>
                <Plus size={12} /> Add item
              </button>
            )}
          </div>

          {mode === 'view' ? (
            <div className="overflow-x-auto border border-slate-200 rounded-lg">
               <table className="w-full text-[11px] bg-white">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-left text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
                      <th className="py-3 px-4">Product</th>
                      <th className="py-3 px-4">Variant</th>
                      <th className="py-3 px-4">Details</th>
                      <th className="py-3 px-4 text-right">Price</th>
                      <th className="py-3 px-4 text-center">Flat Discount (₹)</th>
                      <th className="py-3 px-4 text-center">Discount Percent (%)</th>
                      <th className="py-3 px-4 text-center">Free Items</th>
                      <th className="py-3 px-4 text-center">Billed Items</th>
                      <th className="py-3 px-4 text-right">Cost</th>
                      <th className="py-3 px-4 text-right">Gross Amount</th>
                      <th className="py-3 px-4 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(draft.lines || [draft]).map((ln, i) => {
                       const product = products.find((p) => p.id === ln.productId) || null;
                       const cost = calculateCost(ln.originalPrice, ln.discountAmount, ln.discountPercentage, ln.discountType, ln.billedQty);
                       const amt = lineAmt(ln);
                       const grossAmt = Number(ln.originalPrice || 0) * Number(ln.billedQty || 0);
                       return (
                          <tr key={i} className="border-b border-slate-100 last:border-b-0">
                            <td className="py-3 px-4 font-bold text-[#0d0d0d]">{product?.name || ln.productId}</td>
                            <td className="py-3 px-4 text-slate-600">{variantLabelOf(product, ln.variantId)}</td>
                            <td className="py-3 px-4 text-slate-600 capitalize">{ln.purchaseType}{ln.purchaseType === 'unit' ? ' (Unit)' : ''}</td>
                            <td className="py-3 px-4 text-slate-700 text-right">₹{(Number(ln.originalPrice) || 0).toFixed(2)}</td>
                            <td className="py-3 px-4 text-slate-700 text-center">{Number(ln.discountAmount) > 0 ? `₹${ln.discountAmount}` : '—'}</td>
                            <td className="py-3 px-4 text-slate-700 text-center">{Number(ln.discountPercentage) > 0 ? `${ln.discountPercentage}%` : '—'}</td>
                            <td className="py-3 px-4 text-slate-700 text-center">{Number(ln.bonusQty) > 0 ? ln.bonusQty : '—'}</td>
                            <td className="py-3 px-4 text-slate-700 text-center">{ln.billedQty || 0}</td>
                            <td className="py-3 px-4 text-slate-700 text-right">₹{cost.toFixed(2)}</td>
                            <td className="py-3 px-4 text-slate-700 text-right font-bold">₹{grossAmt.toFixed(2)}</td>
                            <td className="py-3 px-4 text-slate-700 text-right font-bold" style={{ color: accent }}>₹{amt.toFixed(2)}</td>
                          </tr>
                       );
                    })}
                  </tbody>
               </table>
            </div>
          ) : (draft.lines || [draft]).map((ln, i) => {
            const product = products.find((p) => p.id === ln.productId) || null;
            const variants = product?.variants || [];
            
            const udArray = typeof ln.unitDetails === 'string' ? JSON.parse(ln.unitDetails || '[]') : (ln.unitDetails || []);
            const purchaseLevel = ln.selectedPurchaseLevelIndex || 0;
            const totalMultiplier = ln.purchaseType === 'unit' ? udArray.slice(purchaseLevel).reduce((acc, u) => acc * (Number(u.quantity) || 1), 1) : 1;
            const totalBaseItems = ((Number(ln.billedQty) || 0) + (Number(ln.bonusQty) || 0)) * totalMultiplier;
            const amount = lineAmt(ln);
            
            const selectedUnitName = udArray.length > purchaseLevel && udArray[purchaseLevel].unitId ? getUnitName(udArray[purchaseLevel].unitId) : '';
            const qtyLabelSuffix = ln.purchaseType === 'item' ? 'Item' : selectedUnitName;

            return (
              <div key={i} className="rounded-lg border border-slate-200 bg-slate-50 p-5 flex flex-col gap-4 relative shadow-sm">
                
                <div className="flex items-center justify-between border-b border-slate-200 pb-3">
                  <div className="flex items-center gap-6">
                    <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 bg-white px-2 py-1 rounded shadow-sm">Item {i + 1}</span>
                    <label className="flex items-center gap-2 font-bold text-slate-600 cursor-pointer uppercase text-xs">
                      <input type="radio" checked={ln.purchaseType === 'item'} onChange={() => setLine(i, { purchaseType: 'item' })} className="accent-[var(--theme-primary)]" /> PURCHASE BY ITEM
                    </label>
                    <label className="flex items-center gap-2 font-bold text-slate-600 cursor-pointer uppercase text-xs">
                      <input type="radio" checked={ln.purchaseType === 'unit'} onChange={() => {
                        setLine(i, { purchaseType: 'unit', selectedPurchaseLevelIndex: 0 });
                      }} className="accent-[var(--theme-primary)]" /> PURCHASE BY UNIT
                    </label>
                  </div>
                  <button type="button" onClick={() => removeLine(i)} disabled={(draft.lines || [draft]).length <= 1} className="p-1.5 rounded-full text-slate-400 hover:text-rose-500 hover:bg-rose-50 disabled:opacity-30 disabled:hover:bg-transparent transition-colors">
                    <IconX size={14} />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Product *" bg="bg-white" error={errors[`line_${i}_productId`]}>
                    <CustomSelect
                      value={ln.productId}
                      onChange={(v) => { setLine(i, { productId: v, variantId: '' }); setErrors({ ...errors, [`line_${i}_productId`]: undefined }); }}
                      placeholder="Select a product…"
                      options={products.map((p) => ({ value: p.id, label: p.name }))}
                    />
                  </Field>
                  <Field label="Variant" bg="bg-white">
                    <CustomSelect
                      value={ln.variantId}
                      onChange={(v) => setLine(i, { variantId: v })}
                      disabled={!variants.length}
                      placeholder={variants.length ? 'Standard / none' : 'No variants'}
                      options={variants.map((v) => ({ value: v.id, label: v.label }))}
                    />
                  </Field>
                </div>

                {ln.purchaseType === 'unit' && (
                  <div className="bg-white rounded-lg border border-slate-200 overflow-hidden shadow-sm mt-2">
                    <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between gap-4">
                      <span className="text-[12px] font-bold uppercase tracking-[0.1em] text-slate-500">Unit Hierarchy</span>
                      <div className="flex items-center gap-2 w-full max-w-[320px]">
                        {udArray.length > 0 && !(udArray.length === 1 && !udArray[0].unitId) && (
                          <button
                            type="button"
                            onClick={() => setLine(i, { isCustomUnitHierarchy: !ln.isCustomUnitHierarchy })}
                            className={`p-2 shrink-0 rounded-lg border transition-colors ${ln.isCustomUnitHierarchy ? 'bg-blue-50 border-blue-200 text-blue-600 shadow-sm' : 'bg-white border-slate-200 text-slate-400 hover:text-slate-600 hover:bg-slate-50'}`}
                            title={ln.isCustomUnitHierarchy ? "Lock Multipliers" : "Edit Multipliers"}
                          >
                            <Pen size={16} />
                          </button>
                        )}
                        <div className="flex-1">
                          <CustomSelect
                            value={ln.selectedUnitHierarchyId || ''}
                            onChange={(v) => {
                              const updates = { selectedUnitHierarchyId: v };
                              const selectedUnit = globalUnits.find(u => u.id === v);
                              let levels = [];
                              if (selectedUnit && selectedUnit.hierarchy) {
                                 try {
                                   const hierarchy = typeof selectedUnit.hierarchy === 'string' ? JSON.parse(selectedUnit.hierarchy) : selectedUnit.hierarchy;
                                   levels = hierarchy.levels || [];
                                 } catch (e) {
                                   console.error("Failed to parse hierarchy JSON", e);
                                 }
                              }
                              if (selectedUnit && levels.length > 0) {
                                const newUdArray = levels.map(lvl => ({
                                  unitId: lvl.name || 'Level',
                                  quantity: Number(lvl.multiplier) || 1,
                                  subUnitId: ''
                                }));
                                updates.unitDetails = JSON.stringify(newUdArray);
                                updates.selectedPurchaseLevelIndex = 0;
                              } else {
                                updates.unitDetails = '[]';
                                updates.selectedPurchaseLevelIndex = 0;
                              }
                              setLine(i, updates);
                            }}
                            options={globalUnits.map(gu => ({ value: gu.id, label: gu.name }))}
                            placeholder="Select Unit Hierarchy..."
                          />
                        </div>
                      </div>
                    </div>
                    {udArray.length === 0 || (udArray.length === 1 && !udArray[0].unitId) ? (
                      <div className="py-8 border-b border-slate-200 text-center text-[11px] font-bold uppercase tracking-[0.1em] text-slate-400 bg-slate-50/50">
                        Please select a Unit Hierarchy to view its hierarchy.
                      </div>
                    ) : (
                      <div className="overflow-auto custom-scrollbar max-h-[400px] border-b border-slate-200">
                      <div className="flex flex-col lg:flex-row items-stretch min-w-[800px]">
                        {/* Left: Table */}
                        <div className="flex-1 border-r border-slate-200">
                          <table className="w-full text-left border-collapse bg-white">
                            <thead className="bg-slate-50/90 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-20 shadow-sm">
                            <tr className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500">
                              <th className="py-2 px-3 text-center">Level</th>
                              <th className="py-2 px-3">Unit</th>
                              <th className="py-2 px-3 text-center">Contains</th>
                              <th className="py-2 px-3">Next Unit</th>
                              <th className="py-2 px-3 whitespace-nowrap">Total In Base Unit</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {udArray.map((ud, uIdx) => {
                              const isLast = uIdx === udArray.length - 1;
                              const nextUnitId = uIdx < udArray.length - 1 ? udArray[uIdx+1].unitId : '';
                              const nextUnitName = nextUnitId ? getUnitName(nextUnitId) : 'Base Unit';
                              
                              let runMult = 1;
                              for (let j = uIdx + 1; j < udArray.length; j++) {
                                 runMult *= (Number(udArray[j].quantity) || 1);
                              }
                              const totalStr = uIdx === udArray.length - 1 ? `1 Base Unit` : `${runMult.toLocaleString()} Base Unit`;

                              return (
                                <tr key={uIdx} className="bg-white hover:bg-slate-50/30 transition-colors h-[56px]">
                                  <td className="py-2 px-3 text-center">
                                    <div className="flex justify-center">
                                      <button 
                                        type="button"
                                        onClick={() => setLine(i, { selectedPurchaseLevelIndex: uIdx })}
                                        className={`inline-flex w-6 h-6 items-center justify-center rounded-full font-extrabold text-[10px] cursor-pointer transition-colors ${
                                          (ln.selectedPurchaseLevelIndex || 0) === uIdx ? 'bg-[var(--theme-primary)] text-white shadow-md' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                                        }`}
                                      >
                                        {uIdx + 1}
                                      </button>
                                    </div>
                                  </td>
                                  <td className="py-2 px-3">
                                    <div className="w-full text-left px-2 py-1.5 rounded-md bg-transparent text-[#0d0d0d] font-bold text-xs select-none">
                                      {ud.unitId ? getUnitName(ud.unitId) : '—'}
                                    </div>
                                  </td>
                                  <td className="py-2 px-3">
                                    {isLast ? (
                                      <div className="w-full text-center px-2 py-1.5 border border-slate-100 rounded-md bg-slate-100/50 text-slate-400 font-bold text-xs select-none">-</div>
                                    ) : (
                                      <div className="w-full flex justify-center">
                                        {ln.isCustomUnitHierarchy ? (
                                          <input
                                            type="number"
                                            min="1"
                                            value={ud.quantity}
                                            onChange={(e) => {
                                              const newVal = parseInt(e.target.value, 10) || 1;
                                              const newUdArray = [...udArray];
                                              newUdArray[uIdx] = { ...newUdArray[uIdx], quantity: newVal };
                                              setLine(i, { unitDetails: JSON.stringify(newUdArray) });
                                            }}
                                            className="w-20 text-center px-2 py-1.5 rounded-md text-slate-700 font-bold text-[13px] bg-white border border-slate-300 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-500/20 transition-all shadow-sm"
                                          />
                                        ) : (
                                          <div className="w-20 text-center px-2 py-1.5 rounded-md text-slate-700 font-bold text-[13px] select-none">
                                            {ud.quantity}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </td>
                                  <td className="py-2 px-3">
                                    {isLast ? (
                                      <div className="w-full text-left px-3 py-1.5 border border-slate-200 rounded-md bg-white text-slate-500 font-bold text-xs select-none">Base Unit</div>
                                    ) : (
                                      <div className="w-full text-left px-2 py-1.5 border border-slate-200 rounded-md bg-white shadow-sm text-[#0d0d0d] font-extrabold text-xs flex items-center justify-between pointer-events-none opacity-80">
                                        <span>{nextUnitName}</span>
                                        <ArrowDown size={12} className="text-slate-400" />
                                      </div>
                                    )}
                                  </td>
                                  <td className="py-2 px-3">
                                    <div className="flex items-center justify-between w-full">
                                      <span className="font-extrabold text-slate-700 text-xs whitespace-nowrap">{totalStr}</span>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Right: Flow Diagram */}
                      <div className="flex flex-col bg-slate-50/30 w-full lg:w-[250px] shrink-0">
                        <div className="bg-slate-50/90 backdrop-blur-sm border-b border-slate-200 flex items-center justify-center shrink-0 sticky top-0 z-20 shadow-sm h-[33px]">
                          <div className="py-2 px-3 text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500 text-center">Hierarchy Flow</div>
                        </div>
                        <div className="flex flex-col items-center justify-start w-full pb-2">
                        
                        {udArray.map((ud, uIdx) => {
                          const nextUnitId = uIdx < udArray.length - 1 ? udArray[uIdx+1].unitId : '';
                          const nextUnitName = nextUnitId ? getUnitName(nextUnitId) : 'Base Unit';
                          const isLast = uIdx === udArray.length - 1;
                          const currentName = ud.unitId ? getUnitName(ud.unitId) : 'Select Unit';
                          return (
                            <React.Fragment key={`flow-${uIdx}`}>
                              <div className="bg-white border border-slate-200 shadow-sm rounded-lg px-3 py-1.5 flex flex-col items-center min-w-[120px] transition-colors mt-2">
                                <span className="font-bold text-[12px] text-[#0d0d0d] capitalize">{currentName}</span>
                                {isLast ? (
                                  <span className="text-[10px] font-medium text-slate-500">Base Unit</span>
                                ) : (
                                  <span className="text-[10px] font-medium text-slate-500">{ud.quantity} {nextUnitName}</span>
                                )}
                              </div>
                              {!isLast && (
                                <div className="w-px h-5 bg-slate-300 relative mt-2">
                                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[4px] border-r-[4px] border-t-[5px] border-l-transparent border-r-transparent border-t-slate-400"></div>
                                </div>
                              )}
                            </React.Fragment>
                          )
                        })}
                        </div>
                      </div>
                    </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="bg-white rounded-lg border border-slate-200 overflow-hidden shadow-sm mt-2">
                  <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between gap-4">
                    <span className="text-[12px] font-bold uppercase tracking-[0.1em] text-slate-500">Purchase & Pricing</span>
                  </div>
                  <div className="p-4 grid grid-cols-2 md:grid-cols-6 gap-4">
                    <Field label={qtyLabelSuffix ? `Pur Qty (${qtyLabelSuffix})` : 'Pur Qty'} bg="bg-slate-50" error={errors[`line_${i}_billedQty`]}>
                      <input type="number" min="1" value={ln.billedQty} onChange={(e) => {
                        const val = e.target.value;
                        setLine(i, { billedQty: val === '' ? '' : Math.max(0, parseFloat(val) || 0) });
                        setErrors({ ...errors, [`line_${i}_billedQty`]: undefined });
                      }} />
                    </Field>
                    <Field label={qtyLabelSuffix ? `Price per (${qtyLabelSuffix})` : 'Price per Item'} bg="bg-slate-50" error={errors[`line_${i}_originalPrice`]}>
                      <input type="number" min="0" value={ln.originalPrice} onChange={(e) => {
                        const val = e.target.value;
                        setLine(i, { originalPrice: val === '' ? '' : Math.max(0, parseFloat(val) || 0) });
                        setErrors({ ...errors, [`line_${i}_originalPrice`]: undefined });
                      }} />
                    </Field>
                    <Field label="Discount Type" bg="bg-slate-50">
                      <CustomSelect
                        value={ln.discountType}
                        onChange={(v) => setLine(i, { discountType: v })}
                        placeholder="Select type..."
                        options={[
                          { value: 'flat', label: 'Flat' },
                          { value: 'percentage', label: 'Percentage' },
                          { value: 'product', label: 'Product' }
                        ]}
                      />
                    </Field>

                    {ln.discountType !== 'product' && (
                      <Field label="Actual Amount" bg="bg-slate-100">
                        <input type="number" value={+(Number(ln.billedQty || 0) * Number(ln.originalPrice || 0)).toFixed(2)} readOnly className="cursor-not-allowed text-slate-500 bg-transparent font-bold" />
                      </Field>
                    )}

                    {ln.discountType === 'flat' && (
                      <Field label="Flat Amount" icon={IndianRupee} bg="bg-slate-50">
                        <input type="number" min="0" value={ln.discountAmount} onChange={(e) => {
                          const val = e.target.value;
                          setLine(i, { discountAmount: val === '' ? '' : Math.max(0, parseFloat(val) || 0) });
                        }} />
                      </Field>
                    )}
                    {ln.discountType === 'percentage' && (
                      <Field label="Percentage" icon={Percent} bg="bg-slate-50">
                        <input type="number" min="0" value={ln.discountPercentage} onChange={(e) => {
                          const val = e.target.value;
                          setLine(i, { discountPercentage: val === '' ? '' : Math.max(0, parseFloat(val) || 0) });
                        }} />
                      </Field>
                    )}
                    {ln.discountType === 'product' && (
                      <Field label="Free Quantity" icon={Package} bg="bg-slate-50">
                        <input type="number" min="0" value={ln.totalFreeQty || ''} onChange={(e) => {
                          const val = e.target.value;
                          setLine(i, { totalFreeQty: val === '' ? '' : Math.max(0, parseFloat(val) || 0) });
                        }} />
                      </Field>
                    )}
                    
                    <Field label="Total Amount" bg="bg-slate-100">
                      <input type="number" value={+((() => {
                        const gross = Number(ln.billedQty || 0) * Number(ln.originalPrice || 0);
                        if (ln.discountType === 'flat') return Math.max(0, gross - (Number(ln.discountAmount) || 0));
                        if (ln.discountType === 'percentage') return Math.max(0, gross - (gross * (Number(ln.discountPercentage) || 0) / 100));
                        return gross;
                      })()).toFixed(2)} readOnly className="cursor-not-allowed text-slate-500 bg-transparent font-extrabold" style={{ color: accent }} />
                    </Field>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-6 pt-2 border-t border-slate-200 mt-2">
                  <div className="text-right">
                    <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-1">Total Items (Base)</p>
                    <p className="text-lg font-extrabold text-[#0d0d0d]">{totalBaseItems}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-1">Total Cost</p>
                    <p className="text-xl font-extrabold" style={{ color: accent }}>₹{amount}</p>
                  </div>
                </div>

              </div>
            );
          })}
          
          {mode !== 'view' && (
            <button
              type="button" onClick={addLine}
              className="w-full py-4 border-2 border-dashed border-slate-300 rounded-lg text-slate-500 font-bold uppercase tracking-[0.14em] hover:border-[var(--theme-primary)] hover:text-[var(--theme-primary)] hover:bg-slate-50 transition-all flex items-center justify-center gap-2"
            >
              <Plus size={16} strokeWidth={3}/> Add another item
            </button>
          )}

        </div>
      </div>
      
      <footer className="px-6 py-4 bg-slate-50 border-t border-[var(--theme-tertiary)] flex items-center justify-between">
        <div className="text-right">
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-1">Order Total</p>
          <p className="text-2xl font-extrabold" style={{ color: accent }}>
            ₹{(draft.lines || [draft]).reduce((sum, ln) => sum + lineAmt(ln), 0)}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button type="button" onClick={onClose} className="px-5 py-2 rounded-lg text-xs font-semibold uppercase tracking-[0.14em] text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 transition-colors">Cancel</button>
          {mode !== 'view' && (
            <button
              type="button"
              onClick={handleFormSubmit}
              className="px-5 py-2 rounded-lg text-xs font-semibold uppercase tracking-[0.14em] text-white transition-opacity disabled:opacity-50 hover:opacity-90 flex items-center justify-center shadow-md"
              style={{ background: accent }}
            >
              {mode === 'edit' ? 'Save Changes' : 'Confirm Purchase'}
            </button>
          )}
        </div>
      </footer>
    </motion.div>
  );
};
export const WhPurchasesTab = ({ warehouseId, org, showNotification }) => {
  const warehouse = useMemo(() => findWarehouse(warehouseId), [warehouseId]);
  const [version, setVersion] = useState(0);
  const bump = () => setVersion((v) => v + 1);
  const purchase = useMemo(() => findWhPurchase(warehouseId), [warehouseId, version]);
  const allOrders = useMemo(() => whPurchaseOrdersOf(warehouseId).filter(po => ['Pending', 'Received'].includes(po.status)), [warehouseId, version]);
  const products = useMemo(() => catalogProductsOf(org.id), [org.id]);
  const suppliers = useMemo(() => suppliersOf(org.id), [org.id]);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({});

  const orders = useMemo(() => {
    let result = allOrders;
    if (filters.statuses && filters.statuses.length > 0) {
      result = result.filter(o => filters.statuses.includes((o.status || '').toLowerCase()));
    }
    if (filters.suppliers && filters.suppliers.length > 0) {
      result = result.filter(o => filters.suppliers.includes(o.supplier || ''));
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(o => (o.supplier || '').toLowerCase().includes(q));
    }
    return result;
  }, [allOrders, search, filters]);

  const [globalUnits, setGlobalUnits] = useState([]);
  useEffect(() => {
    fetchWithAuth('/org/units')
      .then(res => { if (res.ok) return res.json(); throw new Error('Network response was not ok'); })
      .then(data => setGlobalUnits(data || []))
      .catch(err => console.error('Failed to fetch units:', err));
  }, []);

  const [modalMode, setModalMode] = useState('create');
  const [draft, setDraft] = useState(blankPO(warehouseId, org.id));
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [viewReceipt, setViewReceipt] = useState(null);
  
  // 'list' | 'form'
  const [viewMode, setViewMode] = useState('list');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  
  const totalPages = Math.max(1, Math.ceil(orders.length / perPage));
  const start = (page - 1) * perPage;
  const pageOrders = orders.slice(start, start + perPage);


  if (!warehouse) {
    return (
      <div className="py-16 text-center text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">
        Warehouse not found.
      </div>
    );
  }

  const accent = org.theme.primary;
  const openCreate = () => {
    // `blankPO` already seeds an empty supplier. This previously spread
    // `supplier: purchase?.supplier || ''` over the top, referencing a
    // `purchase` that is not declared in this file — optional chaining does not
    // help when the identifier itself is missing, so the ReferenceError fired on
    // every click of New purchase order.
    setDraft(blankPO(warehouseId, org.id));
    setModalMode('create');
    setViewMode('form');
  };
  const openEdit = (po) => { 
    const draftPO = JSON.parse(JSON.stringify(po));
    if (draftPO.lines && draftPO.lines.length > 0) {
      // lines already exist — keep all values as-is (don't zero out)
      draftPO.lines = draftPO.lines.map(ln => ({
        productId: ln.productId || '',
        variantId: ln.variantId || '',
        purchaseType: ln.purchaseType || 'item',
        originalPrice: ln.originalPrice ?? '',
        discountType: ln.discountType || 'percentage',
        discountPercentage: ln.discountPercentage ?? '',
        discountAmount: ln.discountAmount ?? '',
        billedQty: ln.billedQty ?? 0,
        totalFreeQty: ln.totalFreeQty ?? ln.bonusQty ?? '',
        unitDetails: ln.unitDetails || '[]',
        selectedUnitHierarchyId: ln.selectedUnitHierarchyId || '',
        selectedPurchaseLevelIndex: ln.selectedPurchaseLevelIndex || 0,
        isCustomUnitHierarchy: ln.isCustomUnitHierarchy || false,
      }));
    } else {
      // flat PO — build lines from top-level fields
      draftPO.lines = [{
        productId: draftPO.productId || '',
        variantId: draftPO.variantId || '',
        purchaseType: draftPO.purchaseType || 'item',
        originalPrice: draftPO.originalPrice ?? '',
        discountType: draftPO.discountType || 'percentage',
        discountPercentage: draftPO.discountPercentage ?? '',
        discountAmount: draftPO.discountAmount ?? '',
        billedQty: draftPO.billedQty ?? draftPO.qty ?? 0,
        totalFreeQty: draftPO.totalFreeQty ?? draftPO.bonusQty ?? '',
        unitDetails: draftPO.unitDetails || '[]',
        selectedUnitHierarchyId: draftPO.selectedUnitHierarchyId || '',
        selectedPurchaseLevelIndex: draftPO.selectedPurchaseLevelIndex || 0,
        isCustomUnitHierarchy: draftPO.isCustomUnitHierarchy || false,
      }];
    }
    setDraft(draftPO); 
    setModalMode('edit'); 
    setViewMode('form'); 
  };
  const openView = (po) => { setViewReceipt(po); };

  const submitPO = async (record) => {
    // Automatically save any custom units edited in this purchase order to the Unit Master
    const linesToProcess = record.lines || [record];
    for (let i = 0; i < linesToProcess.length; i++) {
      const ln = linesToProcess[i];
      if (ln.isCustomUnitHierarchy && ln.unitDetails && ln.unitDetails !== '[]') {
        try {
          const udArray = JSON.parse(ln.unitDetails);
          const getUnitName = (id) => globalUnits.find(u => u.id === id)?.name || id || 'Unit';
          const customName = udArray.map((u, idx) => {
            const name = getUnitName(u.unitId);
            if (idx === udArray.length - 1) return name;
            return `${name}(${u.quantity})`;
          }).join(' - ');
          
          await fetchWithAuth('/org/units', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              orgId: org.id,
              name: customName,
              hierarchy: JSON.stringify({ levels: udArray.map(u => ({ name: u.unitId, multiplier: u.quantity })) })
            })
          });
        } catch (e) {
          console.error("Failed to automatically save custom unit master:", e);
        }
      }
    }

    const finalLines = (record.lines || []).map(ln => {
      let multiplier = 1;
      if (ln.purchaseType === 'unit' && ln.unitDetails && ln.unitDetails !== '[]') {
        try {
          const rawUd = JSON.parse(ln.unitDetails);
          const udArray = Array.isArray(rawUd) ? rawUd : (rawUd.hierarchy || []);
          const lvlIdx = ln.selectedPurchaseLevelIndex !== undefined ? ln.selectedPurchaseLevelIndex : (rawUd.selectedIndex || 0);
          for (let j = lvlIdx; j < udArray.length; j++) {
            if (udArray[j] && !isNaN(udArray[j].quantity)) {
              multiplier *= Number(udArray[j].quantity) || 1;
            }
          }
          
          const getUnitName = (id) => globalUnits.find(u => u.id === id)?.name || id || 'Unit';
          const purchaseUnit = udArray.length > lvlIdx ? getUnitName(udArray[lvlIdx].unitId) : 'Unit';
          const baseUnit = udArray.length > 0 ? getUnitName(udArray[udArray.length - 1].unitId) : 'Item';
          
          ln.unitDetails = JSON.stringify({
            hierarchy: udArray,
            selectedIndex: lvlIdx,
            purchaseUnit: purchaseUnit,
            baseUnit: baseUnit,
            multiplier: multiplier
          });
        } catch (e) {
          console.error("Error calculating multiplier:", e);
        }
      }

      const unitCost = calculateCost(ln.originalPrice, ln.discountAmount, ln.discountPercentage, ln.discountType, ln.billedQty);
      const baseCost = multiplier > 0 ? unitCost / multiplier : unitCost;

      return {
        ...ln,
        costPrice: baseCost,
        bonusQty: (Number(ln.totalFreeQty) || 0) * multiplier,
        qty: ((Number(ln.billedQty) || 0) + (Number(ln.totalFreeQty) || 0)) * multiplier,
      };
    });

    const totalQty = finalLines.reduce((acc, ln) => acc + (ln.qty || 0), 0);
    const totalAmount = finalLines.reduce((acc, ln) => acc + lineAmt(ln), 0);

    const payload = {
      ...record,
      qty: totalQty,
      amount: totalAmount,
      lines: finalLines
    };

    if (!record.supplier) {
      if (showNotification) showNotification('Supplier is required.', 'error');
      return;
    }

    if (!record.lines || record.lines.length === 0) {
      if (showNotification) showNotification('Please add at least one product.', 'error');
      return;
    }
    
    if (record.lines.some(ln => !ln.productId)) {
      if (showNotification) showNotification('Please select a product for all line items.', 'error');
      return;
    }

    if (modalMode === 'create') {
      addWhPurchaseOrder(payload);
      if (showNotification) showNotification('Purchase order added.', 'success');
    } else {
      updateWhPurchaseOrder(record.id, payload);
      if (showNotification) showNotification('Purchase order updated.', 'success');
    }
    setViewMode('list');
    setVersion((v) => v + 1);
  };

  const doDelete = () => {
    if (!confirmDelete) return;
    deleteWhPurchaseOrder(confirmDelete.id);
    if (showNotification) showNotification('Purchase order removed.', 'success');
    setConfirmDelete(null);
    setVersion((v) => v + 1);
  };

  return (
    <div className="flex flex-col gap-4 pb-2">
      {viewMode === 'form' ? (
        <POForm
          draft={draft}
          setDraft={setDraft}
          onClose={() => setViewMode('list')}
          onSubmit={submitPO}
          mode={modalMode}
          accent={accent}
          products={products}
          suppliers={suppliers}
          globalUnits={globalUnits}
        />
      ) : (
        <>
          {/* Search + Filter Bar */}
          <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg px-5 py-4 flex flex-col gap-3 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex flex-1 min-w-0 items-center gap-3 flex-wrap">
                {/* Search */}
                <div className="relative flex-1 max-w-md">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
                  <input
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by supplier…"
                    className="w-full bg-[var(--theme-secondary)] border border-[var(--theme-tertiary)] focus:bg-white focus:border-[var(--theme-primary)] rounded-lg pl-9 pr-4 py-2 text-xs font-medium text-[#0d0d0d] outline-none transition-colors"
                  />
                </div>
                {/* Filter Popover */}
                <div className="shrink-0 flex items-center">
                  <FilterPopover
                    accent={accent}
                    initialFilters={filters}
                    onApply={(f) => { setFilters(f); setPage(1); }}
                    categories={[
                      {
                        id: 'statuses',
                        label: 'Status',
                        options: [
                          { id: 'pending', label: 'Pending' },
                          { id: 'received', label: 'Received' },
                        ]
                      },
                      ...(suppliers.length > 0 ? [{
                        id: 'suppliers',
                        label: 'Supplier',
                        options: suppliers.map(s => ({ id: s.supplierName, label: s.supplierName }))
                      }] : [])
                    ]}
                  />
                </div>
              </div>
              {/* New Order Button */}
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={openCreate}
                  disabled={products.length === 0}
                  title={products.length === 0 ? 'Catalog has no products yet' : undefined}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em] shadow-lg transition-opacity disabled:opacity-40 hover:opacity-90"
                  style={{ background: accent, boxShadow: `0 12px 24px -12px ${accent}` }}
                >
                  <Plus size={14} aria-hidden="true" /> New Order
                </button>
              </div>
            </div>
          </section>

          {/* Orders Table */}
          <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 border-b border-[var(--theme-tertiary)]">
                  <tr>
                    <th className="px-4 py-3">Supplier</th>
                    <th className="px-3 py-3">Details</th>
                    <th className="px-3 py-3">Date</th>
                    <th className="px-3 py-3">Time</th>
                    <th className="px-3 py-3 text-center">Qty</th>
                    <th className="px-3 py-3 text-right">Amount</th>
                    <th className="px-3 py-3">Status</th>
                    <th className="px-3 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--theme-tertiary)]">
                  {pageOrders.map((po) => {
                    const dt = formatDateTime(po.date);
                    let dPart = dt;
                    let tPart = '—';
                    if (dt.includes(', ')) [dPart, tPart] = dt.split(', ');
                    const statusColor = po.status === 'Received' ? '#0a7d3c' : '#b45309';
                    return (
                      <tr key={po.id} className="hover:bg-slate-50 group group transition-colors cursor-pointer" onClick={() => openView(po)}>
                        <td className="px-4 py-3 text-[12px] font-extrabold text-[#0d0d0d]">
                          {po.supplier || '—'}
                        </td>
                        <td className="px-3 py-3 text-[12px] font-extrabold text-slate-600">
                          {(() => {
                            const types = Array.from(new Set((po.lines || [po]).map(ln => (ln.purchaseType || 'item').toLowerCase())));
                            if (types.length === 0) return '-';
                            if (types.length === 1) return types[0].charAt(0).toUpperCase() + types[0].slice(1);
                            return 'Mixed';
                          })()}
                        </td>
                        <td className="px-3 py-3 text-[12px] font-medium text-slate-600 whitespace-nowrap">{dPart}</td>
                        <td className="px-3 py-3 text-[12px] font-medium text-slate-400 whitespace-nowrap">{tPart}</td>
                        <td className="px-3 py-3 text-center text-[12px] font-extrabold text-[#0d0d0d]">
                          {po.qty || 0}
                        </td>
                        <td className="px-3 py-3 text-[14px] font-extrabold text-[#0d0d0d] text-right">₹{(po.amount || 0).toFixed(2)}</td>
                        <td className="px-3 py-3 text-[12px] font-extrabold uppercase tracking-[0.14em]">
                          <span className={`${po.status === 'Received' ? 'text-emerald-600' : po.status === 'Pending' ? 'text-amber-600' : 'text-slate-600'}`}>
                            {po.status || 'Pending'}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-right">
                          <RowActions onClick={(e) => e.stopPropagation()}>
                            <IconAction icon={Eye} label={`View order from ${po.supplier}`} title="View" onClick={() => openView(po)} />
                            <IconAction icon={Pen} label={`Edit order from ${po.supplier}`} title="Edit" onClick={() => openEdit(po)} />
                            <IconAction icon={Trash2} tone="danger" label={`Remove order from ${po.supplier}`} title="Remove" onClick={() => setConfirmDelete(po)} />
                          </RowActions>
                        </td>
                      </tr>
                    );
                  })}
                  {orders.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-16 text-center text-xs font-semibold uppercase tracking-[0.16em] text-slate-300">No purchase orders found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Footer */}
            {orders.length > 0 && (
              <section className="flex items-center justify-between border-t border-[var(--theme-tertiary)] px-5 py-3">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">Showing</span>
                  <CustomSelect
                    value={perPage}
                    onChange={(val) => { setPerPage(Number(val)); setPage(1); }}
                    options={[
                      { value: 10, label: '10' }, { value: 25, label: '25' },
                      { value: 50, label: '50' }, { value: 100, label: '100' },
                      { value: orders.length > 0 ? orders.length : 200, label: 'All' }
                    ]}
                    className="w-16 bg-white border border-[var(--theme-tertiary)] rounded-sm px-2 py-1 text-[11px] font-bold text-slate-700 outline-none hover:border-[var(--theme-primary)] transition-colors cursor-pointer"
                    menuUp={true}
                  />
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">
                    PAGE <strong className="text-slate-800 mx-0.5">{page}</strong> OF <strong className="text-slate-800 ml-0.5">{totalPages}</strong>
                  </span>
                  <div className="flex items-center gap-1.5">
                    <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="w-8 h-8 rounded-md bg-white border border-[var(--theme-tertiary)] flex items-center justify-center text-slate-500 hover:text-[var(--theme-primary)] hover:border-[var(--theme-primary)] disabled:opacity-40 transition-all shadow-sm"><ChevronLeft size={14} /></button>
                    <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="w-8 h-8 rounded-md bg-white border border-[var(--theme-tertiary)] flex items-center justify-center text-slate-500 hover:text-[var(--theme-primary)] hover:border-[var(--theme-primary)] disabled:opacity-40 transition-all shadow-sm"><ChevronRight size={14} /></button>
                  </div>
                </div>
              </section>
            )}
          </section>
        </>
      )}

      <AnimatePresence>
        {viewReceipt && (
          <motion.section
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[130] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-7xl flex flex-col max-h-[90vh] overflow-hidden"
            >
              <header className="px-6 py-4 flex items-center justify-between border-b border-slate-200">
                <div>
                  <h3 className="text-lg font-extrabold text-[#0d0d0d] flex items-center gap-2">
                    <Package size={20} style={{ color: org.theme.primary }} /> Purchase Order Details
                  </h3>
                  <p className="text-xs text-slate-500 font-medium mt-1">
                    {viewReceipt.supplier || 'Unknown Supplier'} &bull; {viewReceipt.date ? formatDateTime(viewReceipt.date) : 'No Date'} &bull; {viewReceipt.status}
                  </p>
                </div>
                <button type="button" onClick={() => setViewReceipt(null)} className="p-2 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors">
                  <IconX size={20} />
                </button>
              </header>

              <div className="flex-1 overflow-auto p-6 bg-slate-50">
                <div className="overflow-x-auto border border-slate-200 rounded-lg">
                  <table className="w-full text-[11px] bg-white">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr className="text-left text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
                        <th className="py-3 px-4">Product</th>
                        <th className="py-3 px-4">Variant</th>
                        <th className="py-3 px-4">Details</th>
                        <th className="py-3 px-4 text-right">Price</th>
                        <th className="py-3 px-4 text-center">Flat Discount (₹)</th>
                        <th className="py-3 px-4 text-center">Discount Percent (%)</th>
                        <th className="py-3 px-4 text-center">Free Items</th>
                        <th className="py-3 px-4 text-center">Billed Items</th>
                        <th className="py-3 px-4 text-right">Cost</th>
                        <th className="py-3 px-4 text-right">Gross Amount</th>
                        <th className="py-3 px-4 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(viewReceipt.lines || [viewReceipt]).map((ln, i) => {
                        const product = products.find((p) => p.id === ln.productId) || null;
                        const cost = calculateCost(ln.originalPrice, ln.discountAmount, ln.discountPercentage, ln.discountType, ln.billedQty);
                        const amt = lineAmt(ln);
                        const grossAmt = Number(ln.originalPrice || 0) * Number(ln.billedQty || 0);
                        
                        let dPart = '—';
                        let tPart = '—';
                        if (viewReceipt.date) {
                          const dt = formatDateTime(viewReceipt.date);
                          if (dt.includes(', ')) {
                            [dPart, tPart] = dt.split(', ');
                          } else {
                            dPart = dt;
                          }
                        }

                        return (
                          <tr key={i} className="border-b border-slate-100 last:border-b-0">
                            <td className="py-3 px-4 font-bold text-[#0d0d0d]">{product?.name || ln.productId}</td>
                            <td className="py-3 px-4 text-slate-600">{variantLabelOf(product, ln.variantId)}</td>
                            <td className="py-3 px-4 text-slate-600 capitalize">{ln.purchaseType}{ln.purchaseType === 'unit' ? ' (Unit)' : ''}</td>
                            <td className="py-3 px-4 text-slate-700 text-right">₹{(Number(ln.originalPrice) || 0).toFixed(2)}</td>
                            <td className="py-3 px-4 text-slate-700 text-center">{Number(ln.discountAmount) > 0 ? `₹${ln.discountAmount}` : '—'}</td>
                            <td className="py-3 px-4 text-slate-700 text-center">{Number(ln.discountPercentage) > 0 ? `${ln.discountPercentage}%` : '—'}</td>
                            <td className="py-3 px-4 text-slate-700 text-center">{Number(ln.bonusQty) > 0 ? ln.bonusQty : '—'}</td>
                            <td className="py-3 px-4 text-slate-700 text-center">{ln.billedQty || 0}</td>
                            <td className="py-3 px-4 text-slate-700 text-right">₹{cost.toFixed(2)}</td>
                            <td className="py-3 px-4 text-slate-700 text-right font-bold">₹{grossAmt.toFixed(2)}</td>
                            <td className="py-3 px-4 text-slate-700 text-right font-bold" style={{ color: org.theme.primary }}>₹{amt.toFixed(2)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              
              <footer className="px-6 py-4 border-t border-[var(--theme-tertiary)] flex items-center justify-between shrink-0 bg-white">
                <div className="text-right">
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-1">Order Total</p>
                  <p className="text-2xl font-extrabold" style={{ color: org.theme.primary }}>
                    ₹{(viewReceipt.lines || [viewReceipt]).reduce((s, ln) => s + lineAmt(ln), 0).toFixed(2)}
                  </p>
                </div>
                <button type="button" onClick={() => setViewReceipt(null)} className="px-6 py-2.5 rounded-lg font-bold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 transition-colors">
                  Close
                </button>
              </footer>
            </motion.div>
          </motion.section>
        )}
      </AnimatePresence>

      <ConfirmModal
        open={!!confirmDelete}
        title="Delete Purchase Order"
        message="Are you sure you want to delete this purchase order? This action cannot be undone."
        onConfirm={doDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
};

WhPurchasesTab.propTypes = {
  warehouseId: PropTypes.string.isRequired,
  org: PropTypes.object.isRequired,
  showNotification: PropTypes.func,
};
